import React, { useCallback } from 'react';
import { cn } from '@/utils/cn';
import { menuPage } from '@/data/content';
import { trackEvent } from '@/Services/analytics';
import Button from '@/components/ui/Button';

interface Category {
    id: string | number;
    category_name: string;
    ficon?: string;
}

const DrawerSectionCard: React.FC<{
    icon: string;
    title: string;
    children: React.ReactNode;
    className?: string;
}> = ({ icon, title, children, className = '' }) => (
    <section className={cn('border border-app-border rounded-2xl p-4 bg-app-surface', className)}>
        <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden="true">
                {icon}
            </span>
            <h3 className="text-xs font-black uppercase tracking-widest text-app-muted">{title}</h3>
        </div>
        {children}
    </section>
);

const FiltersPanel: React.FC<{
    categories: Category[];
    activeCategories: (string | number)[];
    onToggleCategory: (id: string | number) => void;
    includeOutOfStock: boolean;
    onToggleStock: (v: boolean) => void;
    handleReset: () => void;
    hasActiveFilters: boolean;
    resultCount?: number;
    onClose: () => void;
}> = ({
    categories,
    activeCategories,
    onToggleCategory,
    includeOutOfStock,
    onToggleStock,
    handleReset,
    hasActiveFilters,
    resultCount,
    onClose,
}) => (
    <>
        <div className="flex-1 overflow-y-auto relative p-5 space-y-6">
            <DrawerSectionCard icon="🔖" title={menuPage.filter_category_label}>
                <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => {
                        const isActive = activeCategories.some((c) => String(c) === String(cat.id));
                        return (
                            <Button
                                variant="outline"
                                key={cat.id}
                                type="button"
                                onClick={() => onToggleCategory(cat.id)}
                                className={cn(
                                    'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-200 border-2',
                                    isActive
                                        ? 'border-app-primary text-app-primary bg-app-primary/5'
                                        : 'border-transparent bg-app-bg text-app-text hover:bg-app-border'
                                )}
                            >
                                {cat.ficon && <span>{cat.ficon}</span>}
                                {cat.category_name}
                            </Button>
                        );
                    })}
                </div>
            </DrawerSectionCard>

            <DrawerSectionCard icon="📦" title={menuPage.filter_in_stock}>
                <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-bold border-2 border-app-primary bg-app-primary/5 text-app-primary cursor-default">
                        <input type="checkbox" checked readOnly className="accent-app-primary w-4 h-4" />
                        <span className="text-base">✅</span>
                        {menuPage.filter_in_stock}
                    </label>
                    <label className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-bold border-2 border-app-border bg-app-surface text-app-text cursor-pointer hover:border-app-primary/40 transition-colors">
                        <input
                            type="checkbox"
                            checked={includeOutOfStock}
                            onChange={(e) => onToggleStock(e.target.checked)}
                            className="accent-app-primary w-4 h-4"
                        />
                        <span className="text-base">🚫</span>
                        {menuPage.filter_include_oos}
                    </label>
                </div>
            </DrawerSectionCard>
        </div>

        <div className="p-5 border-t border-app-border bg-app-bg">
            <div className="flex gap-3">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleReset}
                    disabled={!hasActiveFilters}
                    className="flex-1 !shadow-none hover:!translate-y-0"
                >
                    {menuPage.filter_reset}
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={onClose}
                    className="flex-1"
                >
                    {resultCount !== undefined
                        ? menuPage.filter_results_count.replace('{count}', String(resultCount))
                        : menuPage.filter_apply}
                </Button>
            </div>
        </div>
    </>
);

interface MenuFilterDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    categories: Category[];
    activeCategories: (string | number)[];
    onToggleCategory: (id: string | number) => void;
    includeOutOfStock: boolean;
    onToggleStock: (v: boolean) => void;
    resultCount?: number;
    onResetFilters: () => void;
}

const MenuFilterDrawer: React.FC<MenuFilterDrawerProps> = ({
    isOpen,
    onClose,
    categories,
    activeCategories,
    onToggleCategory,
    includeOutOfStock,
    onToggleStock,
    resultCount,
    onResetFilters,
}) => {
    const handleReset = useCallback(() => {
        trackEvent('filter_reset');
        onResetFilters();
    }, [onResetFilters]);

    const hasActiveFilters = activeCategories.length > 0 || includeOutOfStock;

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1040] transition-opacity duration-300"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Mobile bottom sheet */}
            <div
                className="md:hidden fixed bottom-0 left-0 right-0 w-full max-h-[85vh] rounded-t-3xl bg-app-surface shadow-2xl z-[1045] flex flex-col animate-in slide-in-from-bottom duration-300"
                role="dialog"
                aria-label={menuPage.filter_label}
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1.5 rounded-full bg-app-border" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-app-border">
                    <h2 className="text-lg font-bold text-app-text">{menuPage.filter_drawer_title}</h2>
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={onClose}
                        aria-label={menuPage.close_label}
                        className="!p-2 -mr-2 rounded-lg"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </Button>
                </div>

                <FiltersPanel
                    categories={categories}
                    activeCategories={activeCategories}
                    onToggleCategory={onToggleCategory}
                    includeOutOfStock={includeOutOfStock}
                    onToggleStock={onToggleStock}
                    handleReset={handleReset}
                    hasActiveFilters={hasActiveFilters}
                    resultCount={resultCount}
                    onClose={onClose}
                />
            </div>

            {/* Desktop drawer */}
            <div
                className="hidden md:flex fixed top-0 right-0 bottom-0 w-full max-w-md bg-app-surface shadow-2xl z-[1045] flex-col animate-in slide-in-from-right duration-300 border-l border-app-border"
                role="dialog"
                aria-label={menuPage.filter_label}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-app-border">
                    <h2 className="text-xl font-bold text-app-text">{menuPage.filter_drawer_title}</h2>
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={onClose}
                        aria-label={menuPage.close_label}
                        className="!p-2 -mr-2 rounded-lg"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </Button>
                </div>

                <FiltersPanel
                    categories={categories}
                    activeCategories={activeCategories}
                    onToggleCategory={onToggleCategory}
                    includeOutOfStock={includeOutOfStock}
                    onToggleStock={onToggleStock}
                    handleReset={handleReset}
                    hasActiveFilters={hasActiveFilters}
                    resultCount={resultCount}
                    onClose={onClose}
                />
            </div>
        </>
    );
};

export default MenuFilterDrawer;
